import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./PublicationPage.css";
import "../components/PublicationPdfLightbox.css";
import * as api from "../api/writtenContribution";
import { WrittenContribution } from "../hooks/useWrittenContributions";
import {
  handleAddCommentHelper,
  handleDeleteCommentHelper,
  handleEditCommentHelper,
  sortCommentsByDate,
} from "../utils/commentHelpers";
import CommentItem from "../components/CommentItem";
import { useAuthUser } from "../components/dashboard/hooks/useAuthUser";
import {
  getContributorDisplayName,
  getAuthorIdFromRef,
} from "../utils/contributorDisplayName";
import LoadingSpinner from "../components/LoadingSpinner";
import PublicationPdfViewer from "../components/PublicationPdfViewer";
import PublicationPdfLightbox from "../components/PublicationPdfLightbox";
import PublicationArticleEmbed from "../components/PublicationArticleEmbed";

const PublicationPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { token, userId: currentUserId, isSuperUser, isAdmin } =
    useAuthUser();
  const isStaffModerator = isSuperUser || isAdmin;

  const [publication, setPublication] = useState<WrittenContribution | null>(
    null,
  );
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [newCommentText, setNewCommentText] = useState<string>("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const loadPublication = async () => {
      try {
        setLoading(true);
        if (!slug) {
          setError("Missing publication slug");
          return;
        }

        const data = await api.callFetchContributionBySlug(slug);
        setPublication(data);

        if (data.comments && Array.isArray(data.comments)) {
          setComments(sortCommentsByDate(data.comments));
        }
      } catch (err: any) {
        console.error("Error loading publication:", err);
        setError(err.message || "Failed to load publication");
      } finally {
        setLoading(false);
      }
    };

    loadPublication();
  }, [slug]);

  useEffect(() => {
    setNumPages(0);
    setPageNumber(1);
    setRenderedPages(new Set());
    setIsFullscreen(false);
  }, [publication?.fileUrl]);

  const onDocumentLoadSuccess = (totalPages: number) => {
    setNumPages(totalPages);
    setPageNumber(1);
    setRenderedPages(new Set());
  };

  const handlePdfLoadError = (err: Error) => {
    console.error("PDF load error:", err);
    setError("Failed to load PDF: " + (err?.message || String(err)));
  };

  const onPageRenderSuccess = (page: number) => {
    setRenderedPages((prev) => {
      if (prev.has(page)) return prev;
      const next = new Set(prev);
      next.add(page);
      return next;
    });
  };

  const canGoNext = pageNumber < numPages && renderedPages.has(pageNumber + 1);
  const canGoPrev = pageNumber > 1 && renderedPages.has(pageNumber - 1);

  const nextPage = () => {
    if (canGoNext) setPageNumber((p) => p + 1);
  };

  const prevPage = () => {
    if (canGoPrev) setPageNumber((p) => p - 1);
  };

  const handleAddComment = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!token) return navigate("/login");
    if (!publication) return;

    await handleAddCommentHelper(
      publication._id,
      newCommentText,
      token,
      setComments,
      setNewCommentText,
      setError,
    );
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!token) return navigate("/login");
    if (!publication) return;

    await handleDeleteCommentHelper(
      publication._id,
      commentId,
      token,
      setComments,
      () => {},
      setError,
    );
  };

  const handleEditComment = async (commentId: string, text: string) => {
    if (!token) return navigate("/login");
    if (!publication) return;

    await handleEditCommentHelper(
      publication._id,
      commentId,
      text,
      token,
      setComments,
      () => {},
      setError,
    );
  };

  const publicationAuthorId = publication
    ? getAuthorIdFromRef(publication.author)
    : "";
  const isPublicationAuthor =
    !!publication &&
    !!currentUserId &&
    !!publicationAuthorId &&
    currentUserId === publicationAuthorId;

  if (loading) {
    return <LoadingSpinner className="loading-spinner-container--fullscreen" />;
  }
  if (error) return <p className="text-danger">Error: {error}</p>;
  if (!publication)
    return (
      <div className="content-container">
        <div className="info-card">
          <p>Publication not found.</p>
        </div>
      </div>
    );

  const isExternalLink = publication.mimeType === "text/uri-list";

  return (
    <div className="content-container publication-container">
      <div className="publication-compact-info">
        <h2 className="publication-header">{publication.title}</h2>
        <p className="publication-summary">{publication.summary}</p>
        {publication.summaryEditedByAdmin && (
          <p className="publication-summary-admin-note">
            This summary has been edited by an Admin.
          </p>
        )}
        <p>Published {new Date(publication.createdAt).toLocaleDateString()}</p>
        <p className="bold capitalized">
          {getContributorDisplayName(
            publication.author,
            publication.authorDisplayName,
          )}
        </p>
      </div>

      <div
        className={`pdf-viewer-shell${isFullscreen ? " pdf-viewer-shell--fullscreen" : ""}`}
      >
        {isExternalLink ? (
          <PublicationArticleEmbed
            title={publication.title}
            articleUrl={publication.fileUrl}
          />
        ) : (
          <PublicationPdfViewer
            fileUrl={publication.fileUrl}
            pageNumber={pageNumber}
            onPageNumberChange={setPageNumber}
            numPages={numPages}
            onDocumentLoadSuccess={onDocumentLoadSuccess}
            renderedPages={renderedPages}
            onPageRenderSuccess={onPageRenderSuccess}
            onLoadError={handlePdfLoadError}
            showExpandButton={!isFullscreen}
            onExpandClick={() => setIsFullscreen(true)}
          />
        )}
      </div>

      {!isExternalLink && (
        <PublicationPdfLightbox
          isOpen={isFullscreen}
          title={publication.title}
          pageNumber={pageNumber}
          numPages={numPages}
          canGoPrev={canGoPrev}
          canGoNext={canGoNext}
          onPrev={prevPage}
          onNext={nextPage}
          onClose={() => setIsFullscreen(false)}
        />
      )}

      <section className="publication-comments" aria-label="Comments">
        <h3 className="publication-comments__heading">Comments</h3>

        {token ? (
          <form className="publication-comment-compose" onSubmit={handleAddComment}>
            <textarea
              className="publication-comment-compose__input"
              rows={3}
              placeholder="Share your thoughts..."
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              aria-label="Write a comment"
            />
            <div className="publication-comment-compose__footer">
              <button className="btn" type="submit" disabled={!newCommentText.trim()}>
                Post comment
              </button>
            </div>
          </form>
        ) : (
          <div className="info-card publication-comments__login-prompt">
            <p>Sign up or log in to join the conversation.</p>
            <p>
              <a href="/login">Login</a> or <a href="/signup">Sign up</a>
            </p>
          </div>
        )}

        <div className="publication-comments__list">
          {comments.length === 0 ? (
            <div className="info-card publication-comments__empty">
              <p>No comments yet. Be the first to comment.</p>
            </div>
          ) : (
            comments.map((c: any) => {
              const commentAuthorId = getAuthorIdFromRef(c.author);

              const canEdit =
                currentUserId &&
                commentAuthorId &&
                currentUserId === commentAuthorId;
              const canDelete =
                currentUserId &&
                commentAuthorId &&
                (currentUserId === commentAuthorId ||
                  isPublicationAuthor ||
                  isStaffModerator);

              return (
                <CommentItem
                  key={c._id}
                  comment={c}
                  canDelete={!!canDelete}
                  canEdit={!!canEdit}
                  onDelete={handleDeleteComment}
                  onEdit={handleEditComment}
                />
              );
            })
          )}
        </div>
      </section>
    </div>
  );
};

export default PublicationPage;
